// frontend/src/http-common.js
import axios from "axios";

/**
 *
 * prefers an explicit REACT_APP_API_BASE_URL (e.g. /api),
 * otherwise normalizes REACT_APP_SERVER_URI to an absolute origin (adds the protocol if missing),
 * defaults to same-origin /api,
 * turns on withCredentials for session cookies,
 * fixes AUTH to match.
 *
 **/
const API_BASE = (() => {
	// Avoid `import.meta` so Jest/CommonJS can parse this file.
	// Prefer a runtime override (window.__RAVEN_API_BASE_URL), otherwise use REACT_APP_* envs.
	const serverUri = (process.env.REACT_APP_SERVER_URI || "").trim().replace(/\/$/, "");
	const rawExplicit =
		(typeof window !== "undefined" && window.__RAVEN_API_BASE_URL) ||
		process.env.REACT_APP_API_BASE_URL ||
		"";
	const explicit = rawExplicit.trim().replace(/\/$/, "");
	if (explicit) {
		if (/^https?:\/\//i.test(explicit)) {
			return explicit;
		}
		if (explicit.startsWith("/") && serverUri) {
			const origin = /^https?:\/\//i.test(serverUri)
				? serverUri
				: `${typeof window !== "undefined" ? window.location.protocol : "http:"}//${serverUri}`;
			return `${origin}${explicit}`;
		}
		return explicit; // "/api"
	}

	if (serverUri) {
		// If srv has no scheme, prefix current protocol (avoids "/host/api" paths)
		const origin = /^https?:\/\//i.test(serverUri)
			? serverUri
			: `${typeof window !== "undefined" ? window.location.protocol : "http:"}//${serverUri}`;
		return `${origin}/api`;
	}

	return "/api"; // same-origin default
})();

const AUTH_BASE = API_BASE.replace(/\/api$/, "/auth");

const API = axios.create({
	baseURL: API_BASE,
	withCredentials: true,
	headers: {
		"Content-Type": "application/json",
	},
});

const AUTH = axios.create({
	baseURL: AUTH_BASE,
	withCredentials: true,
	headers: {
		"Content-Type": "application/json",
	},
});

let unauthorizedHandler = null;
let unauthorizedHandling = false;

export const setApiUnauthorizedHandler = (handler) => {
	unauthorizedHandler = typeof handler === "function" ? handler : null;
};

export const isUnauthenticated401 = (error) => {
	const status = error?.response?.status;
	const code = String(error?.response?.data?.error || "").toLowerCase();
	// Only treat canonical session-expired response as forced-logout signal.
	return status === 401 && code === "unauthenticated";
};

if (API?.interceptors?.response?.use) {
	API.interceptors.response.use(
		(response) => response,
		(error) => {
			if (isUnauthenticated401(error) && unauthorizedHandler && !unauthorizedHandling) {
				// Single-flight guard: avoid cascading logout calls from bursty parallel 401 responses.
				unauthorizedHandling = true;
				try {
					unauthorizedHandler(error);
				} catch (_e) {
					// Never block the original request rejection on handler failures.
				} finally {
					// Keep a short lock window to avoid burst-triggering multiple logout flows.
					setTimeout(() => {
						unauthorizedHandling = false;
					}, 1500);
				}
			}
			return Promise.reject(error);
		}
	);
}

export default API;
export { AUTH };
